import type { TMenuTab } from 'src/components/navigation/sidebar-tabs.component'
import Sidebar from 'src/components/navigation/sidebar.component'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useTranslation } from 'react-i18next'
import {
  AccountIcon,
  DashboardIcon,
  GraphIcon,
  PatientsIcon,
  ReviewsIcon,
  SwitchIcon,
  UsersIcon,
} from '@honestica/curecall-ui'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { useAuth } from 'src/hooks/use-auth'

const MedicalSidebar = () => {
  const { project, isFeatureActivated } = useCurrentProject()
  const { currentUser } = useAuth()
  const { t } = useTranslation()

  const medicalMenuTabs: (TMenuTab | null)[] = [
    {
      path: `/${project.id}/medical/dashboard`,
      label: t('components.navigation.dashboard'),
      IconComponent: DashboardIcon,
      requiredFeature: ['dashboard', 'dashboard-v2'],
      // No `requiredPermission` because this is the default page for the medical space, the user will always land
      // on this page when going to "medical", it may have access to other elements but not this one. This item should
      // still be displayed to avoid confusing the user
    },
    isFeatureActivated('is-planning-patient-activated')
      ? {
          path: `/${project.id}/medical/planning`,
          label: t('components.navigation.patients'),
          IconComponent: PatientsIcon,
        }
      : {
          path: `/${project.id}/medical/patients`,
          label: t('components.navigation.patient-list'),
          IconComponent: PatientsIcon,
          // No `requiredPermission` because this is the default page for the medical space, the user will always land
          // on this page when going to "medical", it may have access to other elements but not this one. This item should
          // still be displayed to avoid confusing the user
        },
    {
      path: `/${project.id}/medical/reviews`,
      label: t('components.navigation.patient-reviews'),
      IconComponent: ReviewsIcon,
      requiredPermission: {
        subject: EPermissionGrantSubjects.PATIENT_REVIEWS,
        action: EPermissionGrantActions.GET,
      },
    },
    {
      path: `/${project.id}/medical/account`,
      label: t('components.navigation.my-account'),
      IconComponent: AccountIcon,
    },
    {
      path: `/${project.id}/medical/statistics`,
      label: t('components.navigation.statistics'),
      IconComponent: GraphIcon,
      requiredPermission: {
        subject: EPermissionGrantSubjects.STATISTICS,
        action: EPermissionGrantActions.GET,
      },
    },
    {
      path: `/${project.id}/medical/users`,
      label: t('components.navigation.users'),
      IconComponent: UsersIcon,
      requiredPermission: {
        subject: EPermissionGrantSubjects.USER_MANAGEMENT,
        action: EPermissionGrantActions.GET,
      },
    },
  ]

  if (currentUser?.hasMultiProjects) {
    medicalMenuTabs.push({
      path: `/${project.id}/medical/projects`,
      label: t('components.navigation.projects-list'),
      IconComponent: SwitchIcon,
    })
  }

  return (
    <Sidebar menuTabs={medicalMenuTabs.filter(Boolean) as TMenuTab[]} backgroundClass="c-bg-gradient-02" />
  )
}

export default MedicalSidebar
